/* eslint-disable prefer-destructuring */
import React, { useContext } from 'react';
import { SlideDown } from 'react-slidedown';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { datadogRum } from '@datadog/browser-rum';
import { NavigateToWindowWithNativeEvents } from '@components/_common/link/link';
import ChevronIcon from '../../../assets/ChevronIcon';
import {
  ManufacturerBoldText,
  ManufacturerButton,
  ManufacturerContentButtonWrapper,
  ManufacturerContentTextWrapper,
  ManufacturerContentWarrantiesRow,
  ManufacturerContentWrapper,
  ManufacturerGreyText,
  ManufacturerHeaderTextWrapper,
  ManufacturerHeaderWarrantiesRow,
  ManufacturerWarrantiesHeaderRowItem,
  ManufacturerHeaderWrapper,
  ManufacturerText,
  ManufacturerTitle,
  ManufacturerWarrantiesContentRowItem,
  ManufacturerWarrantyLabel,
  ManufacturerContentPowerWarrantiesRow,
  ManufacturerRowItemWarrantyWrapper,
  ManufacturerRowItemExpiredWarrantyWrapper,
  ManufacturerChevronIconWrapper,
  ManufacturerHeaderLeftSideWrapper,
  ManufacturerHeaderRightSideWrapper,
  ManufacturerContentLeftSideWrapper,
  ManufacturerContentRightSideWrapper,
  ManufacturerExpiredText,
  HideText,
  ListWrapper,
  ListItem,
} from './ManufacturerWarranty.styles';
import { AnalyticEvents } from '../../../constants';

const formatRemainingMilesValue = (value: string) => {
  if (!value) return null;
  const tmpNumValue = value?.toString().replace(/[^0-9]/g, '');
  let commaNumValue = tmpNumValue;

  if (tmpNumValue.length > 3) {
    commaNumValue = Number(tmpNumValue).toLocaleString('en-US');
  }
  return value?.toString().replace(tmpNumValue, commaNumValue);
};

const Expired = ({ dataQa }: any) => (
  <ManufacturerRowItemExpiredWarrantyWrapper>
    <ManufacturerExpiredText data-testid={dataQa}>Expired</ManufacturerExpiredText>
  </ManufacturerRowItemExpiredWarrantyWrapper>
);

const WarrantyInfo = ({ dataQa, durationLeft, protectionMiles }: any) => (
  <>
    <ManufacturerRowItemWarrantyWrapper>
      <ManufacturerGreyText data-testid={`${dataQa}-warranty-info-text`}>
        {durationLeft} | up to {protectionMiles} mi<HideText>les</HideText>
      </ManufacturerGreyText>
    </ManufacturerRowItemWarrantyWrapper>
  </>
);

export const ManufacturerWarrantyHeader = ({ isCardOpen, manufacturer }: any) => {
  const basic = manufacturer[0];
  const powertrain = manufacturer[1];

  let basicFormattedDurationLeft: any = null;
  let powertrainFormattedDurationLeft: any = null;

  if (basic?.durationLeft) {
    basicFormattedDurationLeft = formatRemainingMilesValue(basic.durationLeft);
  }

  if (powertrain?.durationLeft) {
    powertrainFormattedDurationLeft = formatRemainingMilesValue(powertrain.durationLeft);
  }

  const basicFormattedMilesLeft = formatRemainingMilesValue(basic.protectionMiles);

  const powertrainFormattedProtectionMiles = formatRemainingMilesValue(powertrain.protectionMiles);

  return (
    <ManufacturerHeaderWrapper>
      <ManufacturerHeaderLeftSideWrapper>
        <ManufacturerHeaderTextWrapper>
          <ManufacturerTitle data-testid="manufacturer-warranty-title">Manufacturer Warranty</ManufacturerTitle>
          {!isCardOpen && (
            <SlideDown>
              <ManufacturerHeaderWarrantiesRow>
                <ManufacturerWarrantiesHeaderRowItem>
                  <ManufacturerWarrantyLabel>Basic</ManufacturerWarrantyLabel>
                  <>
                    {basic.isExpired ? (
                      <Expired dataQa="manufacturer-warranty-basic-is-expired" />
                    ) : (
                      <WarrantyInfo
                        dataQa="manufacturer-warranty-basic"
                        durationLeft={basicFormattedDurationLeft}
                        protectionMiles={basicFormattedMilesLeft}
                      />
                    )}
                  </>
                </ManufacturerWarrantiesHeaderRowItem>
                <ManufacturerWarrantiesHeaderRowItem>
                  <ManufacturerWarrantyLabel>Powertrain</ManufacturerWarrantyLabel>
                  <>
                    {powertrain.isExpired ? (
                      <Expired dataQa="manufacturer-warranty-powertrain-is-expired" />
                    ) : (
                      <WarrantyInfo
                        dataQa="manufacturer-warranty-powertrain"
                        durationLeft={powertrainFormattedDurationLeft}
                        protectionMiles={powertrainFormattedProtectionMiles}
                      />
                    )}
                  </>
                </ManufacturerWarrantiesHeaderRowItem>
              </ManufacturerHeaderWarrantiesRow>
            </SlideDown>
          )}
        </ManufacturerHeaderTextWrapper>
      </ManufacturerHeaderLeftSideWrapper>

      <ManufacturerHeaderRightSideWrapper>
        <ManufacturerChevronIconWrapper isOpen={isCardOpen}>
          <ChevronIcon />
        </ManufacturerChevronIconWrapper>
      </ManufacturerHeaderRightSideWrapper>
    </ManufacturerHeaderWrapper>
  );
};

export const ManufacturerWarrantyContent = ({ manufacturer, vehicleMake, cardId, cardPosition }: any) => {
  const { trackEvent } = useContext(AnalyticsContext) as any;
  const basic = manufacturer[0];
  const powertrain = manufacturer[1];
  const link = basic.callToAction.link.replace('[make]', vehicleMake);
  const title = basic.callToAction.title;

  let basicFormattedDurationLeft: any = null;
  let powertrainFormattedDurationLeft: any = null;

  if (basic.durationLeft) {
    basicFormattedDurationLeft = formatRemainingMilesValue(basic.durationLeft);
  }

  if (powertrain.durationLeft) {
    powertrainFormattedDurationLeft = formatRemainingMilesValue(powertrain.durationLeft);
  }

  const basicFormattedMilesLeft = formatRemainingMilesValue(basic.protectionMiles);

  const powertrainFormattedProtectionMiles = formatRemainingMilesValue(powertrain.protectionMiles);

  const handleRedirect = () => {
    trackEvent(AnalyticEvents.ovpCardClicked, {
      cardId,
      cardPosition,
      type: [basic.type.toLowerCase(), powertrain.type.toLowerCase()],
      isExpired: [basic.isExpired, powertrain.isExpired],
    });
    datadogRum.addAction(AnalyticEvents.ovpCardClicked, {
      cardId,
      cardPosition,
      type: [basic.type.toLowerCase(), powertrain.type.toLowerCase()],
      isExpired: [basic.isExpired, powertrain.isExpired],
    });
    // note = link goes to google.com, not part of carvana
    NavigateToWindowWithNativeEvents({
      url: link,
      event: 'link',
    });
  };

  return (
    <ManufacturerContentWrapper>
      <ManufacturerContentLeftSideWrapper>
        <ManufacturerContentTextWrapper>
          <ManufacturerText data-testid="manufacturer-warranty-body-paragraph">
            <ManufacturerContentWarrantiesRow>
              <ManufacturerWarrantiesContentRowItem>
                <ManufacturerWarrantyLabel>Basic</ManufacturerWarrantyLabel>
                {basic.isExpired ? (
                  <Expired />
                ) : (
                  <WarrantyInfo durationLeft={basicFormattedDurationLeft} protectionMiles={basicFormattedMilesLeft} />
                )}
              </ManufacturerWarrantiesContentRowItem>
            </ManufacturerContentWarrantiesRow>
            The manufacturer warranty comes with the purchase of your vehicle and includes both basic and powertrain
            warranties.
            <br />
            <br />
            Your basic warranty
            <ManufacturerBoldText>
              &nbsp;covers repair costs due to factory defects or errors.&nbsp;
            </ManufacturerBoldText>
            <br />
            <br />
            This warranty
            <ManufacturerBoldText>&nbsp;doesn’t cover:</ManufacturerBoldText>
            <ListWrapper>
              <ListItem>Routine maintenance, like worn tires and oil changes</ListItem>
              <ListItem>Normal wear and tear</ListItem>
              <ListItem>Accident or environmental damage</ListItem>
              <ListItem>Cosmetics</ListItem>
              <ListItem>Damage from improper care</ListItem>
            </ListWrapper>
            <ManufacturerContentPowerWarrantiesRow>
              <ManufacturerWarrantiesContentRowItem>
                <ManufacturerWarrantyLabel>Powertrain</ManufacturerWarrantyLabel>
                <>
                  {powertrain.isExpired ? (
                    <Expired />
                  ) : (
                    <WarrantyInfo
                      durationLeft={powertrainFormattedDurationLeft}
                      protectionMiles={powertrainFormattedProtectionMiles}
                    />
                  )}
                </>
              </ManufacturerWarrantiesContentRowItem>
            </ManufacturerContentPowerWarrantiesRow>
            The powertrain warranty is also included in the manufacturer warranty and
            <ManufacturerBoldText>
              &nbsp;covers the parts of your vehicle that provide power and make it move.&nbsp;
            </ManufacturerBoldText>
            Components that are commonly covered in the powertrain warranty include the
            <ManufacturerBoldText>&nbsp;engine, transmission,&nbsp;</ManufacturerBoldText>
            and related parts.
            <br />
            <br />
            This warranty
            <ManufacturerBoldText>&nbsp;doesn’t cover</ManufacturerBoldText>
            <ListWrapper>
              <ListItem>Normal wear and tear</ListItem>
              <ListItem>Routine maintenance, like worn tires and oil changes</ListItem>
            </ListWrapper>
          </ManufacturerText>
        </ManufacturerContentTextWrapper>
      </ManufacturerContentLeftSideWrapper>

      <ManufacturerContentRightSideWrapper>
        <ManufacturerContentButtonWrapper>
          <ManufacturerButton data-testid="manufacturer-warranty-cta-button" size="small" onClick={handleRedirect}>
            {title}
          </ManufacturerButton>
        </ManufacturerContentButtonWrapper>
      </ManufacturerContentRightSideWrapper>
    </ManufacturerContentWrapper>
  );
};
